import moment from 'moment';
import { partitionDateRangeByWeek, enumerateCalendarDates, segregateTimeSlots } from './scheduler-util'
import templateUrl from './scheduler-month-calendar.component.html';
import './scheduler-month-calendar.component.less';

/**
 * A calendar that displays dates and timeslots in month format.
 */
export const SchedulerMonthCalendarComponent = {
    templateUrl,
    bindings: {
        monthStart: "<",
        monthEnd: "<",
        schedules: "<",
        editSchedule: "&onEditSchedule",
        deleteSchedule: "&onDeleteSchedule",
        clickTimeSlot: "&onClickTimeSlot", // Pass schedule and optional time slot data.
        doubleClickTimeSlot: "&onDoubleClickTimeSlot", // Pass schedule and optional time slot data.
        createTimeSlot: "&onCreateTimeSlot", // Signal that the scheduler created a time slot on a schedule.
        deleteTimeSlot: "&onDeleteTimeSlot", // Signal that the scheduler is deleting a time slot.
        editTimeSlot: "&onEditTimeSlot", // Signal that the scheduler is editing a time slot.
        moveTimeSlot: "&onMoveTimeSlot" // Propagate event when resizing or moving a time slot between schedules.
    },
    controller: class ScheduleMonthCalendarComponent {
        constructor() {
            this.laneCutoff = 2;
        }
        $onChanges(changesObj) {
            this.schedules = this.schedules || [];
            // Partition the month date range into weeks.
            const weeks = partitionDateRangeByWeek(this.monthStart, this.monthEnd);
            this.calendarDates = weeks.map(week => {
                const dates = enumerateCalendarDates(week.startDate, week.endDate);
                const timeSlots = this.schedules.reduce((timeSlots, schedule) => {
                    for (const timeSlot of schedule.timeSlots) {
                        let startTime = timeSlot.startDateTime;
                        let endTime = timeSlot.endDateTime;
                        if (moment(startTime).isBefore(this.monthStart)) {
                            startTime = moment(this.monthStart).startOf("day").toDate();
                        }
                        if (moment(startTime).isAfter(this.monthStart)) {
                            endTime = moment(this.monthEnd).endOf("day").toDate();
                        }
                        if (moment(timeSlot.startDateTime).isBetween(week.startDate, week.endDate, "week", "[]")) {
                            timeSlots.push({
                                ...timeSlot,
                                startDateTime: startTime,
                                endDateTime: endTime,
                                schedule: {
                                    id: schedule.id,
                                    name: schedule.name,
                                    description: schedule.description,
                                    assigned: schedule.assigned,
                                    deletable: schedule.deletable,
                                    editable: schedule.editable,
                                    readOnly: schedule.readOnly
                                }
                            });
                        }
                    }
                    return timeSlots;
                }, []);

                return {
                    ...week,
                    weekDates: segregateTimeSlots(dates, timeSlots, "day", this.laneCutoff).map(dateLanes => ({
                        date: dateLanes.date,
                        inMonth: moment(dateLanes.date).isBetween(this.monthStart,
                            this.monthEnd, "month", "[]"),
                        lanes: dateLanes.lanes,
                        totalTimeSlots: dateLanes.totalTimeSlots
                    })),
                }
            });
            if (this.calendarDates.length) {
                this.weekDays = this.calendarDates[0].weekDates.map(p => p.date);
            }

        }

        onClickTimeSlot({ schedule, date, timeSlot }, ev) {
            if (timeSlot && !timeSlot.readOnly && (timeSlot.editable || timeSlot.deletable)) {
                const newValue = !timeSlot.showOptions;
                this.schedules.forEach(schedule => schedule.timeSlots
                    .forEach(timeSlot => timeSlot.showOptions = false));
                // Toggle the timeslot options popover.
                timeSlot.showOptions = newValue;
            }

            this.clickTimeSlot({
                $event: {
                    schedule,
                    timeSlot: timeSlot || {
                        startDateTime: moment(date).startOf("day").toDate(),
                        endDateTime: moment(date).endOf("day").toDate()
                    }
                }
            });
        }

        onDoubleClickTimeSlot({ schedule, date, timeSlot }, ev) {
            this.doubleClickTimeSlot({
                $event: {
                    schedule,
                    timeSlot: timeSlot || {
                        startDateTime: moment(date).startOf("day").toDate(),
                        endDateTime: moment(date).endOf("day").toDate()
                    }
                }
            });
        }

        onEditSchedule(schedule) {
            return this.editSchedule({
                $event: {
                    schedule: schedule
                }
            });
        }

        onDeleteSchedule(schedule) {
            return this.deleteSchedule({
                $event: {
                    schedule: schedule
                }
            });
        }

        onDropIntoSchedule(schedule, payload) {
            return this.dropIntoSchedule({
                $event: {
                    schedule,
                    payload
                }
            })
        }

        onDropSchedulePayload({ payload: { schedule, timeSlot }, newSchedule, newDate, dropEffect }) {
            // Handle drop operations. If payload has an id, perform a move.
            // Otherwise, create a new time slot from the payload data.
            if (timeSlot.id && dropEffect !== 'copy') {
                return this.onMoveTimeSlot({ schedule, timeSlot, newSchedule, newDate });
            } else {
                return this.createTimeSlot({
                    $event: {
                        schedule: newSchedule,
                        date: newDate,
                        timeSlot
                    }
                });
            }
        }

        onEditTimeSlot(schedule, timeSlot) {
            return this.editTimeSlot({
                $event: {
                    schedule,
                    timeSlot
                }
            })
        }

        onDeleteTimeSlot(schedule, timeSlot) {
            return this.deleteTimeSlot({
                $event: {
                    schedule,
                    timeSlot
                }
            })
        }

        onMoveTimeSlot({ schedule, timeSlot, newSchedule, newDate }) {
            return this.moveTimeSlot({
                $event: {
                    oldValue: {
                        schedule,
                        timeSlot
                    },
                    newValue: {
                        schedule: newSchedule,
                        timeSlot: {
                            startDateTime: moment(newDate).startOf("day").toDate(),
                            endDateTime: moment(newDate).endOf("day").toDate()
                        }
                    }
                }
            });
        }


        onExpandDate(calendarDate, ev) {
            ev.stopPropagation();
            calendarDate.expanded = !calendarDate.expanded;
        }
    }
};
